/**
 * PAMSSAC Scoring Engine v2
 * Placement, Access, Motivation, Suitability, Susceptibility, Accessibility, Commonalities
 * 
 * Targeting & mobilization readiness overlay for Contact Intelligence
 * Answers: Can we get to the right person now, are they primed to move, and what unlocks them?
 */

import { blink } from '@/lib/blink';
import { scoreCache } from '@/lib/scoreCache';
import { safeJsonParse } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface PAMSSACScore {
  P: number;    // Placement (power to decide)
  A1: number;   // Access (warm paths & channels)
  M: number;    // Motivation (energy to engage)
  S1: number;   // Suitability (ICP & problem fit)
  S2: number;   // Susceptibility (openness to change)
  A2: number;   // Accessibility (practical reachability)
  C: number;    // Commonalities (affinity bridges)
  total: number;
  tier: 'A' | 'B' | 'C';
  weakestTwo: Array<{
    dimension: string;
    score: number;
    why: string;
    evidence: string[];
  }>;
  evidence: Record<string, string[]>;
}

export interface PAMSSACSignals {
  urgency: 'low' | 'medium' | 'high';
  budgetCues: 'approved' | 'seeking' | 'none' | 'unknown';
  decisionWindowDays: number;
  roleClaims: string[];
  incumbentStatus: 'happy' | 'neutral' | 'unhappy' | 'unknown';
  pilotOpenness: 'yes' | 'no' | 'unknown';
  championBehavior: string[];
  blockers: string[];
  accessNotes: string[];
}

// ============================================================================
// Scoring Functions
// ============================================================================

/**
 * Placement: Power to decide/influence
 * P = 0.5*seniority + 0.3*buying_role + 0.2*centrality
 */
function calculatePlacement(
  seniority: number,
  buyingRole: number,
  centrality: number
): number {
  return Math.round(100 * (0.5 * seniority + 0.3 * buyingRole + 0.2 * centrality));
}

/**
 * Access: Warm paths & channels
 * A1 = 0.45*intro_paths + 0.25*intro_success + 0.20*channel_diversity + 0.10*direct_line
 */
function calculateAccess(
  introPaths: number,
  introSuccess: number,
  channelDiversity: number,
  hasDirectLine: boolean
): number {
  const normalize = (x: number, max: number) => Math.min(x / max, 1);
  return Math.round(100 * (
    0.45 * normalize(introPaths, 5) +
    0.25 * introSuccess +
    0.20 * normalize(channelDiversity, 4) +
    0.10 * (hasDirectLine ? 1 : 0)
  ));
}

/**
 * Motivation: Energy to engage now
 * M = 0.4*urgency + 0.25*explicit_asks + 0.2*budget_cues + 0.15*decision_window
 */
function calculateMotivation(
  urgency: 'low' | 'medium' | 'high',
  explicitAsks: number,
  budgetCues: 'approved' | 'seeking' | 'none' | 'unknown',
  decisionWindowDays: number
): number {
  const urgencyMap = { low: 0.3, medium: 0.65, high: 0.9 };
  const budgetMap = { approved: 0.9, seeking: 0.7, none: 0.4, unknown: 0.5 };
  const u = urgencyMap[urgency] || 0.5;
  const b = budgetMap[budgetCues] || 0.5;
  const window = Math.max(0, 90 - Math.min(decisionWindowDays, 90)) / 90;
  const asks = Math.min(explicitAsks / 3, 1);
  
  return Math.round(100 * (0.4 * u + 0.25 * asks + 0.2 * b + 0.15 * window));
}

/**
 * Suitability: ICP & problem fit
 * S1 = 0.6*ICP_fit + 0.4*problem_match
 */
function calculateSuitability(
  icpFit: number,
  problemMatch: number
): number {
  return Math.round(100 * (0.6 * icpFit + 0.4 * problemMatch));
}

/**
 * Susceptibility: Openness to change
 * S2 = 0.35*pilot_open + 0.25*incumbent_dissat + 0.25*champion_behavior - 0.15*blocker_strength
 */
function calculateSusceptibility(
  pilotOpenness: 'yes' | 'no' | 'unknown',
  incumbentDissat: number,
  championBehavior: number,
  blockerStrength: number
): number {
  const pilotMap = { yes: 0.8, no: 0.2, unknown: 0.5 };
  const po = pilotMap[pilotOpenness] || 0.5;
  const cb = Math.min(championBehavior / 3, 1);
  const bs = Math.min(blockerStrength / 3, 1);
  
  return Math.round(100 * (0.35 * po + 0.25 * incumbentDissat + 0.25 * cb - 0.15 * bs));
}

/**
 * Accessibility: Practical reachability
 * A2 = 0.5*Speed + 0.25*AcceptRate + 0.15*OneOnOneRatio - 0.10*Friction
 */
function calculateAccessibility(
  speed: number,
  acceptRate: number,
  oneOnOneRatio: number,
  friction: number
): number {
  return Math.round(100 * (
    0.5 * speed + 
    0.25 * acceptRate + 
    0.15 * oneOnOneRatio - 
    0.10 * friction
  ));
}

/**
 * Commonalities: Affinity bridges
 */
function calculateCommonalities(weightedMatches: number[]): number {
  return Math.round(Math.min(100, weightedMatches.reduce((sum, w) => sum + w, 0)));
}

/**
 * Composite PAMSSAC score
 */
function compositePAMSSAC(
  P: number, A1: number, M: number, S1: number, S2: number, A2: number, C: number
): number {
  return Math.round(
    0.18 * P + 
    0.15 * A1 + 
    0.20 * M + 
    0.15 * S1 + 
    0.12 * S2 + 
    0.10 * A2 + 
    0.10 * C
  );
}

// ============================================================================
// Main Pipeline
// ============================================================================

/**
 * Calculate PAMSSAC score for a contact
 */
export async function calculatePAMSSACScoreV2(
  userId: string,
  contactId: string,
  useCache: boolean = true
): Promise<PAMSSACScore> {
  // Check cache first
  if (useCache) {
    const cached = scoreCache.get<PAMSSACScore>('pamssac', userId, contactId);
    if (cached) {
      console.log(`[PAMSSAC Cache] HIT for contact ${contactId}`);
      return cached;
    }
    console.log(`[PAMSSAC Cache] MISS for contact ${contactId}`);
  }

  // Fetch contact data
  const contact = (await blink.db.contacts.list({
    where: { id: contactId, userId },
    limit: 1
  }))[0];

  if (!contact) {
    throw new Error('Contact not found');
  }

  // Fetch touchpoints for signals
  const emails = await blink.db.messagesEnhanced.list({
    where: { userId, senderId: contactId },
    orderBy: { receivedDate: 'desc' },
    limit: 50,
  });

  const meetings = await blink.db.calendarEventsEnhanced.list({
    where: { userId },
    orderBy: { startTime: 'desc' },
    limit: 20,
  });

  // === Placement ===
  const seniority = inferSeniority(contact);
  const buyingRole = inferBuyingRole(contact);
  const centrality = inferCentrality(meetings);
  const P = calculatePlacement(seniority, buyingRole, centrality);

  // === Access ===
  const introPaths = 1; // Would compute from graph
  const introSuccess = 0.8;
  const channelDiversity = inferChannelDiversity(emails, meetings);
  const hasDirectLine = !!contact.phone;
  const A1 = calculateAccess(introPaths, introSuccess, channelDiversity, hasDirectLine);

  // === Motivation ===
  const urgency = inferUrgency(emails);
  const explicitAsks = countExplicitAsks(emails);
  const budgetCues = inferBudgetCues(emails);
  const decisionWindowDays = inferDecisionWindow(emails);
  const M = calculateMotivation(urgency, explicitAsks, budgetCues, decisionWindowDays);

  // === Suitability ===
  const icpFit = calculateICPFit(contact);
  const problemMatch = 0.7; // Would compute from content analysis
  const S1 = calculateSuitability(icpFit, problemMatch);

  // === Susceptibility ===
  const pilotOpenness = inferPilotOpenness(emails);
  const incumbentDissat = inferIncumbentDissat(emails);
  const championBehavior = countChampionBehavior(emails);
  const blockerStrength = countBlockers(emails);
  const S2 = calculateSusceptibility(pilotOpenness, incumbentDissat, championBehavior, blockerStrength);

  // === Accessibility ===
  const speed = calculateResponseSpeed(emails);
  const acceptRate = calculateAcceptRate(meetings);
  const oneOnOneRatio = calculate1on1Ratio(meetings);
  const friction = 0.2; // Would compute from scheduling difficulty
  const A2 = calculateAccessibility(speed, acceptRate, oneOnOneRatio, friction);

  // === Commonalities ===
  const commonalityMatches = inferCommonalities(contact);
  const C = calculateCommonalities(commonalityMatches);

  // Composite
  const total = compositePAMSSAC(P, A1, M, S1, S2, A2, C);
  const tier: 'A' | 'B' | 'C' = total >= 80 ? 'A' : total >= 60 ? 'B' : 'C';

  // Find weakest two
  const dims = [
    { dimension: 'Placement', key: 'P', score: P, why: 'Power to decide' },
    { dimension: 'Access', key: 'A1', score: A1, why: 'Warm paths' },
    { dimension: 'Motivation', key: 'M', score: M, why: 'Energy to engage' },
    { dimension: 'Suitability', key: 'S1', score: S1, why: 'ICP fit' },
    { dimension: 'Susceptibility', key: 'S2', score: S2, why: 'Openness to change' },
    { dimension: 'Accessibility', key: 'A2', score: A2, why: 'Reachability' },
    { dimension: 'Commonalities', key: 'C', score: C, why: 'Affinity bridges' },
  ];
  dims.sort((a, b) => a.score - b.score);
  const weakestTwo = dims.slice(0, 2).map(d => ({
    dimension: d.dimension,
    score: d.score,
    why: d.why,
    evidence: [`Low ${d.dimension} detected from recent interactions`],
  }));

  const evidence: Record<string, string[]> = {
    P: [`Seniority: ${Math.round(seniority * 100)}%`],
    A1: [`Channel diversity: ${channelDiversity} channels`],
    M: [`Urgency level: ${urgency}`],
    S1: [`ICP fit: ${Math.round(icpFit * 100)}%`],
    S2: [`Pilot openness: ${pilotOpenness}`],
    A2: [`Avg response: ${speed > 0.9 ? '<1h' : speed > 0.7 ? '4h' : '24h'}`],
    C: [`Commonalities found: ${commonalityMatches.length}`],
  };

  const result: PAMSSACScore = {
    P,
    A1,
    M,
    S1,
    S2,
    A2,
    C,
    total,
    tier,
    weakestTwo,
    evidence,
  };

  // Cache the result
  if (useCache) {
    scoreCache.set('pamssac', userId, contactId, result);
    console.log(`[PAMSSAC Cache] SET for contact ${contactId}`);
  }

  return result;
}

// ============================================================================
// Helper Functions
// ============================================================================

function inferSeniority(contact: any): number {
  if (!contact?.name) return 0.5;
  const lower = contact.name.toLowerCase();
  if (/\b(ceo|cto|cfo|coo|founder|partner|president|vp)\b/.test(lower)) return 0.9;
  if (/\b(director|head of|chief)\b/.test(lower)) return 0.7;
  if (/\b(manager|lead|senior)\b/.test(lower)) return 0.6;
  return 0.5;
}

function inferBuyingRole(contact: any): number {
  // Would check for economic buyer, champion, user, blocker tags
  return 0.7;
}

function inferCentrality(meetings: any[]): number {
  const uniqueAttendees = new Set<string>();
  meetings.forEach(m => {
    if (m.attendees) {
      try {
        const attendees = safeJsonParse(m.attendees);
        attendees.forEach((a: any) => uniqueAttendees.add(a.email || a));
      } catch {}
    }
  });
  return Math.min(uniqueAttendees.size / 8, 1);
}

function inferChannelDiversity(emails: any[], meetings: any[]): number {
  let count = 0;
  if (emails.length > 0) count++;
  if (meetings.length > 0) count++;
  return count;
}

function inferUrgency(emails: any[]): 'low' | 'medium' | 'high' {
  const recentEmails = emails.slice(0, 10);
  const urgentKeywords = /\b(urgent|asap|immediately|critical|deadline|this week)\b/i;
  const urgentCount = recentEmails.filter(e => 
    urgentKeywords.test(e.subject || '') || urgentKeywords.test(e.snippet || '')
  ).length;
  
  if (urgentCount >= 3) return 'high';
  if (urgentCount >= 1) return 'medium';
  return 'low';
}

function countExplicitAsks(emails: any[]): number {
  const askPatterns = /\b(can you|could you|would you|please|need|request)\b/i;
  return emails.slice(0, 10).filter(e => 
    askPatterns.test(e.subject || '') || askPatterns.test(e.snippet || '')
  ).length;
}

function inferBudgetCues(emails: any[]): 'approved' | 'seeking' | 'none' | 'unknown' {
  const recentEmails = emails.slice(0, 10);
  const budgetApproved = /\b(approved|signed|budget confirmed|allocated)\b/i;
  const budgetSeeking = /\b(budget|pricing|cost|quote|proposal)\b/i;
  
  if (recentEmails.some(e => budgetApproved.test(e.subject || '') || budgetApproved.test(e.snippet || ''))) {
    return 'approved';
  }
  if (recentEmails.some(e => budgetSeeking.test(e.subject || '') || budgetSeeking.test(e.snippet || ''))) {
    return 'seeking';
  }
  return 'none';
}

function inferDecisionWindow(emails: any[]): number {
  const recentEmails = emails.slice(0, 10);
  const timeframePatterns = /\b(this week|next week|this month|this quarter|Q[1-4])\b/i;
  const hasTimeframe = recentEmails.some(e => 
    timeframePatterns.test(e.subject || '') || timeframePatterns.test(e.snippet || '')
  );
  return hasTimeframe ? 14 : 90;
}

function calculateICPFit(contact: any): number {
  let fit = 0.5;
  if (contact.relationshipType === 'investor' || contact.relationshipType === 'strategic_partner') {
    fit += 0.3;
  } else if (contact.relationshipType === 'customer' || contact.relationshipType === 'prospect') {
    fit += 0.2;
  }
  if (contact.relationshipStage === 'active' || contact.relationshipStage === 'engaged') {
    fit += 0.2;
  }
  return Math.min(fit, 1.0);
}

function inferPilotOpenness(emails: any[]): 'yes' | 'no' | 'unknown' {
  const pilotKeywords = /\b(pilot|poc|proof of concept|trial|test|demo)\b/i;
  const recentEmails = emails.slice(0, 10);
  const hasPilotMention = recentEmails.some(e => 
    pilotKeywords.test(e.subject || '') || pilotKeywords.test(e.snippet || '')
  );
  return hasPilotMention ? 'yes' : 'unknown';
}

function inferIncumbentDissat(emails: any[]): number {
  const dissatKeywords = /\b(frustrated|not working|issues|problems|disappointed|switching)\b/i;
  const recentEmails = emails.slice(0, 10);
  const dissatCount = recentEmails.filter(e => 
    dissatKeywords.test(e.subject || '') || dissatKeywords.test(e.snippet || '')
  ).length;
  return Math.min(dissatCount / 3, 1);
}

function countChampionBehavior(emails: any[]): number {
  const championPatterns = /\b(intro|introduce|forwarding|cc:|meet|connect)\b/i;
  return emails.slice(0, 10).filter(e => 
    championPatterns.test(e.subject || '') || championPatterns.test(e.snippet || '')
  ).length;
}

function countBlockers(emails: any[]): number {
  const blockerKeywords = /\b(legal|compliance|security|procurement|approved vendor|policy)\b/i;
  return emails.slice(0, 10).filter(e => 
    blockerKeywords.test(e.subject || '') || blockerKeywords.test(e.snippet || '')
  ).length;
}

function calculateResponseSpeed(emails: any[]): number {
  // Simplified: assume 50% respond quickly
  return 0.7;
}

function calculateAcceptRate(meetings: any[]): number {
  const accepted = meetings.filter(m => m.responseStatus === 'accepted').length;
  return meetings.length > 0 ? accepted / meetings.length : 0.5;
}

function calculate1on1Ratio(meetings: any[]): number {
  const oneOnOne = meetings.filter(m => {
    if (!m.attendees) return false;
    try {
      const attendees = safeJsonParse(m.attendees);
      return attendees.length <= 2;
    } catch {
      return false;
    }
  }).length;
  return meetings.length > 0 ? oneOnOne / meetings.length : 0.5;
}

function inferCommonalities(contact: any): number[] {
  // Would check for shared geography, alma mater, interests, etc.
  // Return weighted matches
  const matches: number[] = [];
  if (contact.geo?.includes('Ohio')) matches.push(20);
  if (contact.tags?.includes('veteran')) matches.push(15);
  return matches;
}
